import Button from '@/components/Button/Button';
import Header from '@/components/Header/Header';
import { useSTKContext } from '@/context/context';
import {
  getDinamicHeaders,
  getStoreFilteredModifiers,
  getStoreGroups,
  getStores,
} from '@/services/menu/menus.helper.service';
import { useMenuModifiersGroup, useModifierCategories } from '@/services/modifiers/modifiers.service';
import { FilteredModifierValues, Modifier } from '@/types/menus.types';
import { Box, Chip } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './FZStoreMenuModifiersDetails.module.css';
import { useActiveGlobalFreezes } from '@/services/freeze/freeze.service';
import { useCurrentUser } from '@/services/user/user.service';
import useFranchiseeAlias from '@/utils/useFranchiseeAlias/useFranchiseeAlias';
import { RowSelectionState } from '@tanstack/react-table';
import classNames from 'classnames';
import { UserRoles, UserTypes } from '@/types/auth.types';
import FZStoreModifierFilter from '@/pages/Franchisee/Menus/StoreMenuModifiersDetails/internal/FZStoreModifierFilter';

import ModalStores from '@/pages/Franchisee/Menus/StoreMenuDetails/internal/ModalStores.module';
import { CalendarAlert } from '@/utils/CalendarAlert/CalendarAlert';
import { EditPriceModifierDrawer } from '@/pages/Franchisee/Menus/EditPriceDrawer/EditPriceModifierDrawer';
import { ModifierPriceDeviationsDrawer } from '@/pages/Franchisee/Menus/PriceDeviationsDrawer/ModifierPriceDeviationsDrawer';
import FZStoreModifierItemTable from '@/pages/Franchisee/Menus/StoreMenuModifiersDetails/internal/FZStoreModifierItemTable';
import Tooltip from '@/components/Tooltip/Tooltip';
import Icon from '@/components/Icons/Icon';
import { ButtonVariants } from '@/components/Button/Button.types';
import { DeactivateModifiersDrawer } from '@/pages/Franchisee/Menus/DeactivateModifiersDrawer/DeactivateModifiersDrawer';
import {
  getDefaultModifierIdsForCategory,
  getDefaultSubcategorySlotId,
  isModifierCategoryWithModifierMultiSelect,
  isModifierCategoryWithSubcategoryFilter,
  MODIFIER_CATEGORIES_PRICE_UPDATE_ONLY,
  resolveSlotIdForModifierFetch,
  shouldHideInvisibleModifier,
} from '@/pages/Franchisee/Menus/StoreMenuModifiersDetails/modifierDisplay.helpers';
import {
  getModifiersFromRowSelection,
  getModifiersRowFingerprint,
  getSelectedStoreGroupNames,
} from '@/pages/Franchisee/Menus/StoreMenuModifiersDetails/modifierSelection.helpers';
import { useModifierActionButtonState } from '@/pages/Franchisee/Menus/StoreMenuModifiersDetails/useModifierActionButtonState';
import ButtonWithTooltip from '@/pages/Franchisee/Menus/StoreMenuModifiersDetails/internal/ButtonWithTooltip';

/** Modifier categories where franchisees may deactivate modifiers (API-supported). */
const MODIFIER_CATEGORIES_WITH_DEACTIVATE = new Set(['Appetizer Topping', 'Other']);

const FZStoreMenuModifiersDetails = () => {
  const { t } = useTranslation();
  const { alias } = useFranchiseeAlias();
  const { storesAndGroups } = useSTKContext();
  const [stores, setStores] = useState<string[]>(getStores(storesAndGroups));
  const [storeGroups, setStoreGroups] = useState(getStoreGroups(storesAndGroups));
  const [isEditPriceDrawerOpen, setIsEditPriceDrawerOpen] = useState<boolean>(false);
  const [isDeactivateDrawerOpen, setIsDeactivateDrawerOpen] = useState<boolean>(false);
  const [priceDeviationsData, setPriceDeviationsData] = useState<{
    isPriceDeviationsDrawerOpen: boolean;
    modifierCategory: string;
    modifierKeys: {
      modifierId: string;
      slotId: string;
      modifierWeightId: string;
      variantId: string;
    }[];
    selectedGroupName: string;
    cameFromEditDrawer: boolean;
  }>({
    isPriceDeviationsDrawerOpen: false,
    modifierCategory: '',
    modifierKeys: [],
    selectedGroupName: '',
    cameFromEditDrawer: false,
  });
  const [storeModifiers, setStoreModifiers] = useState<Modifier[]>();
  const { data: dataUser } = useCurrentUser(true);
  const { data: activeGlobalFreezeData } = useActiveGlobalFreezes(true);

  const isFranchiseeUserEdit = dataUser?.data.type === UserTypes.FRANCHISEE && dataUser?.data.role === UserRoles.EDIT;
  const isUserViewOnlyAccess = dataUser?.data.role === UserRoles.VIEW;
  const freezeExists = activeGlobalFreezeData?.data && activeGlobalFreezeData?.data?.length > 0;
  const isFranchiseeEditAndFreeze = isFranchiseeUserEdit && freezeExists;

  const [filteredValues, setFilteredValues] = useState<FilteredModifierValues>({});
  const [selectedRows, setSelectedRows] = useState<RowSelectionState>({});
  const [filterApplied, setFilterApplied] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [categoryName, setCategoryName] = useState<string>('');

  const { data: modifierCategoriesRaw = [], isLoading: isModifierCategoriesLoading } = useModifierCategories(true);

  const sortedModifierCategories = useMemo(
    () => [...modifierCategoriesRaw].sort((a, b) => a.displayOrder - b.displayOrder),
    [modifierCategoriesRaw],
  );

  const categoryDropdownOptions = useMemo(
    () => sortedModifierCategories.map((c) => ({ value: c.category, label: c.category })),
    [sortedModifierCategories],
  );

  const defaultModifierCategory = sortedModifierCategories[0]?.category ?? '';

  /** Slots + modifiers for multi-select categories (alphabetical); subcategory shown for Other only. */
  const categorySlotFilterSlots = useMemo(() => {
    if (!isModifierCategoryWithModifierMultiSelect(categoryName)) return [];
    const category = sortedModifierCategories.find((c) => c.category === categoryName);
    if (!category?.slots?.length) return [];
    return [...category.slots]
      .sort((a, b) => a.slotName.localeCompare(b.slotName, undefined, { sensitivity: 'base' }))
      .map((s) => ({
        value: s.slotId,
        label: s.slotName,
        modifiers: [...s.modifiers].sort((a, b) =>
          a.modifierName.localeCompare(b.modifierName, undefined, { sensitivity: 'base' }),
        ),
      }));
  }, [sortedModifierCategories, categoryName]);

  const slotModifierFetchParams = useMemo(() => {
    if (!isModifierCategoryWithModifierMultiSelect(categoryName) || !filterApplied) return null;
    if (!categorySlotFilterSlots.length) return null;

    const subCategory = isModifierCategoryWithSubcategoryFilter(categoryName)
      ? filteredValues.subCategory || getDefaultSubcategorySlotId(categorySlotFilterSlots)
      : filteredValues.subCategory;

    const modifierIds: string[] =
      filteredValues.modifierIds && filteredValues.modifierIds.length > 0
        ? [...filteredValues.modifierIds]
        : filteredValues.modifierId
        ? [filteredValues.modifierId]
        : getDefaultModifierIdsForCategory(
            categorySlotFilterSlots,
            isModifierCategoryWithSubcategoryFilter(categoryName) ? subCategory : undefined,
          );

    if (!modifierIds.length) return null;

    const slotId = resolveSlotIdForModifierFetch(categoryName, categorySlotFilterSlots, subCategory, modifierIds);

    if (!slotId) return null;
    return { slotId, modifierIds };
  }, [
    categoryName,
    filterApplied,
    filteredValues.subCategory,
    filteredValues.modifierIds,
    filteredValues.modifierId,
    categorySlotFilterSlots,
  ]);

  const modifiersQueryEnabled =
    filterApplied &&
    Boolean(categoryName) &&
    (!isModifierCategoryWithModifierMultiSelect(categoryName) || Boolean(slotModifierFetchParams));

  const {
    data: menuModifiersGroup = [],
    isLoading: isStoreLoading,
    isFetching,
    refetch: refetchModifiers,
  } = useMenuModifiersGroup(modifiersQueryEnabled, stores, storeGroups, categoryName, slotModifierFetchParams);

  const [headerWidth, setHeaderWidth] = useState('');

  const changeHeaderWidth = (newWidth: string) => {
    setHeaderWidth(newWidth);
  };
  const applyFilter = (pFilteredValues: FilteredModifierValues) => {
    setFilteredValues({ ...pFilteredValues, category: categoryName });
    setFilterApplied(true);
    setSelectedRows({});
  };
  const handleEditPriceDrawerClose = () => {
    setIsEditPriceDrawerOpen(false);
  };

  const handlePriceDeviationsDrawerClose = () => {
    setPriceDeviationsData({ ...priceDeviationsData, isPriceDeviationsDrawerOpen: false });
  };

  const clearFilter = () => {
    setFilteredValues({});
    setCategoryName(categoryName);
    setSelectedRows({});
  };

  useEffect(() => {
    if (!sortedModifierCategories.length) return;
    const valid = sortedModifierCategories.some((c) => c.category === categoryName);
    if (!categoryName || !valid) {
      setCategoryName(sortedModifierCategories[0].category);
    }
  }, [sortedModifierCategories, categoryName]);

  const selectedStoreGroupNames = useMemo(() => getSelectedStoreGroupNames(storesAndGroups), [storesAndGroups]);

  const modifiers = useMemo(() => {
    const base = getStoreFilteredModifiers(menuModifiersGroup, filteredValues);
    return base.filter((m) => !shouldHideInvisibleModifier(m, selectedStoreGroupNames));
  }, [menuModifiersGroup, filteredValues, selectedStoreGroupNames]);

  /** Row selection is index-based; reset when the visible modifier set/order changes so stale indices cannot apply to new rows. */
  const modifiersRowFingerprint = useMemo(() => getModifiersRowFingerprint(modifiers), [modifiers]);

  useEffect(() => {
    setSelectedRows({});
  }, [modifiersRowFingerprint]);

  const handleEditPriceDrawerOpen = useCallback(() => {
    setStoreModifiers(getModifiersFromRowSelection(selectedRows, modifiers));
    setIsEditPriceDrawerOpen(true);
  }, [modifiers, selectedRows]);

  const handleDeactivateDrawerOpen = useCallback(() => {
    setStoreModifiers(getModifiersFromRowSelection(selectedRows, modifiers));
    setIsDeactivateDrawerOpen(true);
  }, [modifiers, selectedRows]);

  const handleDeactivateDrawerClose = () => {
    setIsDeactivateDrawerOpen(false);
  };

  const handleCategory = (newCategory: string) => {
    setFilteredValues({});
    setCategoryName(newCategory);
  };

  useEffect(() => {
    setSelectedRows({});
  }, [categoryName]);

  useEffect(() => {
    if (!MODIFIER_CATEGORIES_WITH_DEACTIVATE.has(categoryName)) {
      setIsDeactivateDrawerOpen(false);
    }
  }, [categoryName]);

  const selectedModifiersForActions = useMemo(
    () => getModifiersFromRowSelection(selectedRows, modifiers),
    [selectedRows, modifiers],
  );

  const {
    isDeactivateButtonDisabled,
    shouldShowDeactivateTooltip,
    deactivateTooltipMessage,
    isEditPriceButtonDisabled,
    shouldShowEditPriceTooltip,
    editPriceTooltipMessage,
  } = useModifierActionButtonState(
    selectedModifiersForActions,
    Object.keys(selectedRows).length,
    selectedStoreGroupNames,
    t,
  );

  const showDeactivateForCurrentCategory = useMemo(
    () => MODIFIER_CATEGORIES_WITH_DEACTIVATE.has(categoryName),
    [categoryName],
  );

  const primaryEditButtonLabel = useMemo(
    () =>
      MODIFIER_CATEGORIES_PRICE_UPDATE_ONLY.has(categoryName)
        ? t('editMenuPrice.editMenuPrice')
        : t('menu.editMenuPriceButton'),
    [categoryName, t],
  );

  return (
    <div className={styles.page} data-testid="fzStoreModifierMenu">
      <div>
        <div style={{ minWidth: 'fit-content' }}>
          <Header title="Menu" breadcrumb={{ label: t('breadcrumb.backToAllMenus'), to: '/modifiers' }} />
        </div>
        <div className={styles.chip}>
          <Chip
            data-testid="icon"
            key="storeCount"
            label={t('menu.storesLabel', { count: stores?.length ?? 1 })}
            color="default"
            variant="outlined"
            onClick={() => setModalOpen(!modalOpen)}
            sx={{
              fontSize: '14px',
              fontWeight: '500',
              height: '28px',
              backgroundColor: 'white',
              color: '#266DCC',
              borderColor: '#266DCC',
              marginLeft: '160px',
              marginTop: '-135px',
            }}
          />
        </div>
      </div>
      <div className={classNames(styles.header, { [styles.withAlias]: alias })} style={{ minWidth: headerWidth }}>
        <div className={styles.headerLabelAndLegendContainer}>
          <span>{t('menu.filter.modifiers')}</span>
          <span className={styles.legend}>
            <Tooltip
              position="top"
              size={22}
              content={
                <div className={styles.contentColor}>
                  <div className={styles.labelPosition}>{t('menu.legend.dealLovers')}</div>
                  <div>{t('menu.legend.virtualBrand')}</div>
                </div>
              }
            />
            <span className={styles.legendLabel}>{t('menu.legend.legend')}</span>
          </span>
          {!isFranchiseeEditAndFreeze && !isUserViewOnlyAccess && (
            <div className={styles.headerButtonsContainer}>
              {showDeactivateForCurrentCategory && (
                <ButtonWithTooltip
                  showTooltip={shouldShowDeactivateTooltip}
                  tooltipContent={deactivateTooltipMessage}
                  button={
                    <Button
                      variant={ButtonVariants.Primary}
                      onClick={handleDeactivateDrawerOpen}
                      disabled={isDeactivateButtonDisabled}
                    >
                      {shouldShowDeactivateTooltip && (
                        <Icon
                          iconName="AlertCircle"
                          size={20}
                          style={{ marginRight: '8px', color: 'var(--white)', stroke: 'var(--white)' }}
                        />
                      )}
                      {t('deactivateModifiers.title')}
                    </Button>
                  }
                />
              )}
              <ButtonWithTooltip
                showTooltip={shouldShowEditPriceTooltip}
                tooltipContent={editPriceTooltipMessage}
                button={
                  <Button
                    className={styles.editPriceButton}
                    onClick={handleEditPriceDrawerOpen}
                    disabled={isEditPriceButtonDisabled}
                  >
                    {shouldShowEditPriceTooltip && (
                      <Icon
                        iconName="AlertCircle"
                        size={20}
                        style={{ marginRight: '8px', color: 'var(--white)', stroke: 'var(--white)' }}
                      />
                    )}
                    {primaryEditButtonLabel}
                  </Button>
                }
              />
            </div>
          )}
        </div>
      </div>
      <div className={styles.weeklyChangesBanner}>
        <CalendarAlert warningIcon requestedVerbiage={false} addCalendarLink />
      </div>
      <div className={styles.filterActions}>
        <FZStoreModifierFilter
          onApplyFilter={(pFilteredValues: FilteredModifierValues) => applyFilter(pFilteredValues)}
          onClearFilter={clearFilter}
          categories={categoryDropdownOptions}
          categoryName={categoryName}
          defaultCategoryName={defaultModifierCategory}
          onCategoryChange={(newCategory: string) => handleCategory(newCategory)}
          showModifierMultiSelect={isModifierCategoryWithModifierMultiSelect(categoryName)}
          showSubcategoryFilter={isModifierCategoryWithSubcategoryFilter(categoryName)}
          otherCategoryFilterSlots={categorySlotFilterSlots}
          isLoadingCategories={isModifierCategoriesLoading}
          searching={isFetching}
        />
      </div>
      {filterApplied && !isStoreLoading && (
        <Box>
          <Box>
            {isStoreLoading || modifiers.length === 0 ? (
              <Box pl={{ sm: 5, md: 7 }} pt={{ sm: 2, md: 4 }} fontSize="2.0rem">
                <p> No modifiers match your search for these stores </p>
              </Box>
            ) : (
              <FZStoreModifierItemTable
                menuModifiers={modifiers}
                dynamicHeaders={getDinamicHeaders(storesAndGroups)}
                setPriceDeviationsData={setPriceDeviationsData}
                selectedRows={selectedRows}
                onRowSelectionChange={setSelectedRows}
                modifierCategory={categoryName}
                isLoading={false}
                changeHeaderSize={changeHeaderWidth}
                storesAndGroups={storesAndGroups}
                isFranchiseeEditAndFreeze={isFranchiseeEditAndFreeze}
              />
            )}
          </Box>
        </Box>
      )}

      {modalOpen && <ModalStores stores={stores} isOpen={modalOpen} onClose={() => setModalOpen(false)} />}

      {isEditPriceDrawerOpen && (
        <EditPriceModifierDrawer
          isOpen={isEditPriceDrawerOpen}
          onClose={handleEditPriceDrawerClose}
          storeModifiers={storeModifiers || []}
          afterSave={() => {
            setSelectedRows({});
            refetchModifiers();
          }}
          setPriceDeviationsData={setPriceDeviationsData}
          categoryName={categoryName}
        />
      )}
      {isDeactivateDrawerOpen && showDeactivateForCurrentCategory && (
        <DeactivateModifiersDrawer
          isOpen={isDeactivateDrawerOpen}
          onClose={handleDeactivateDrawerClose}
          storeModifiers={storeModifiers || []}
          modifierCategory={categoryName}
          afterSave={() => {
            setSelectedRows({});
            refetchModifiers();
          }}
          setPriceDeviationsData={setPriceDeviationsData}
        />
      )}
      {priceDeviationsData.isPriceDeviationsDrawerOpen && (
        <ModifierPriceDeviationsDrawer
          isOpen={priceDeviationsData.isPriceDeviationsDrawerOpen}
          onClose={handlePriceDeviationsDrawerClose}
          modifierCategory={priceDeviationsData.modifierCategory}
          modifierKeys={priceDeviationsData.modifierKeys}
          selectedGroupName={priceDeviationsData.selectedGroupName}
          cameFromEditDrawer={priceDeviationsData.cameFromEditDrawer}
        />
      )}
    </div>
  );
};
export default FZStoreMenuModifiersDetails;
